import { getGridColumnCount } from './parsePastedGrid';
import { findOverlapsWithOtherTariffs } from './parsePastedPeriods';
import type { ComparableTariff, ExcelPasteIssue, PastedPeriod } from './types';

/*
 * Helpers shared by the pastes of several tariffs at once (a whole room
 * category, every pension tariff, every festive supplement): blocks side by
 * side, label matching, duplicates and overlaps.
 */

export const normalizeTariffName = (name?: string | null) =>
  (name ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('fr');

export interface ExistingTariff {
  name?: string | null;
  /** No label, no date and no price: a card added but never filled. */
  isBlank: boolean;
}

/**
 * Where each pasted tariff goes: the tariff of the same list (category,
 * pension tariffs, supplement) with the same label, else the next blank tariff, else a new tariff after the existing
 * ones. Tariffs the paste does not name are left as they are.
 */
export function matchPastedTariffs<T extends { name: string }>(
  pasted: T[],
  existing: ExistingTariff[],
): {
  targets: Array<{ tariffIndex: number; pasted: T }>;
  newTariffCount: number;
} {
  const used = new Set<number>();
  const byName = pasted.map((tariff) => {
    const index = existing.findIndex(
      ({ name }, position) =>
        !used.has(position) &&
        normalizeTariffName(name) !== '' &&
        normalizeTariffName(name) === normalizeTariffName(tariff.name),
    );
    if (index >= 0) used.add(index);
    return index;
  });

  let newTariffCount = 0;
  const targets = pasted.map((tariff, position) => {
    let tariffIndex = byName[position];
    if (tariffIndex < 0) {
      tariffIndex = existing.findIndex(
        ({ isBlank }, index) => isBlank && !used.has(index),
      );
    }
    if (tariffIndex < 0) {
      tariffIndex = existing.length + newTariffCount;
      newTariffCount += 1;
    }
    used.add(tariffIndex);
    return { tariffIndex, pasted: tariff };
  });

  return { targets, newTariffCount };
}

/**
 * Splits rows copied from Excel into blocks of `width` columns laid side by
 * side, skipping the blocks left entirely empty. `inColumn` is the 1-based
 * position of the block, for messages.
 */
export function splitSideBySideBlocks(
  rows: string[][],
  width: number,
): Array<{ inColumn: number; rows: string[][] }> {
  const columnCount = getGridColumnCount(rows);
  const blocks: Array<{ inColumn: number; rows: string[][] }> = [];
  for (let column = 0; column < columnCount; column += width) {
    const blockRows = rows.map((row) =>
      Array.from({ length: width }, (_, offset) => row[column + offset] ?? ''),
    );
    if (blockRows.some((row) => row.some((cell) => cell.trim() !== ''))) {
      blocks.push({ inColumn: column / width + 1, rows: blockRows });
    }
  }
  return blocks;
}

/** Tags the issues of one block of a side-by-side paste with its position. */
export const tagColumn = (
  issues: ExcelPasteIssue[],
  inColumn: number,
): ExcelPasteIssue[] =>
  issues.map((issue) => ({
    ...issue,
    params: { ...issue.params, inColumn },
  }));

/** One `duplicateTariffLabel` error per label pasted twice. */
export function findDuplicateLabels(labels: string[]): ExcelPasteIssue[] {
  const seen = new Set<string>();
  return labels.flatMap((label) => {
    const key = normalizeTariffName(label);
    const isDuplicate = seen.has(key);
    seen.add(key);
    return isDuplicate
      ? [{ code: 'duplicateTariffLabel' as const, params: { label } }]
      : [];
  });
}

/**
 * Overlap warnings of tariffs pasted at once: each one against the tariffs
 * the paste leaves as they are and the pasted tariffs after it, so a pair is
 * reported once. With more than one pasted tariff, each warning names the
 * pasted tariff it is about (`inTariff`).
 */
export function findPastedOverlaps(
  pasted: Array<{ name: string; periods: PastedPeriod[] }>,
  untouched: ComparableTariff[],
): ExcelPasteIssue[] {
  return pasted.flatMap((tariff, position) => {
    const warnings = findOverlapsWithOtherTariffs(tariff.periods, [
      ...untouched,
      ...pasted.slice(position + 1),
    ]);
    return pasted.length > 1
      ? warnings.map((issue) => ({
          ...issue,
          params: { ...issue.params, inTariff: tariff.name },
        }))
      : warnings;
  });
}

/**
 * Labels of the tariffs a paste adds (new or blank cards), updates, and
 * leaves as they are (blank cards aside). `fallbackName` names an existing
 * tariff without a label.
 */
export function classifyPastedTariffs(
  targets: Array<{ tariffIndex: number; pasted: { name: string } }>,
  existing: ExistingTariff[],
  fallbackName: (index: number) => string,
): { added: string[]; updated: string[]; untouched: string[] } {
  const isAdded = (index: number) =>
    index >= existing.length || existing[index].isBlank;
  const targetIndexes = new Set(targets.map(({ tariffIndex }) => tariffIndex));
  return {
    added: targets
      .filter(({ tariffIndex }) => isAdded(tariffIndex))
      .map(({ pasted }) => pasted.name),
    updated: targets
      .filter(({ tariffIndex }) => !isAdded(tariffIndex))
      .map(({ pasted }) => pasted.name),
    untouched: existing.flatMap(({ name, isBlank }, index) =>
      targetIndexes.has(index) || isBlank ? [] : [name || fallbackName(index)],
    ),
  };
}
